import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import styles from './NestedDropdown.module.css';

const NestedOptions = ({ options, onChange, selectedValues }) => {
  return (
    <>
      {options.map((option, index) => (
        <li key={index}>
          <label className={cn('dropdown-item', styles.liOption)}>
            <input
              type="checkbox"
              value={option.value}
              checked={selectedValues.includes(option.value)}
              onChange={e => {
                onChange(e.target.checked, option.value, option.label);
              }}
              className="me-2"
            />
            <label htmlFor={`checkbox-${option.value}`}>{option.label}</label>
          </label>
        </li>
      ))}
    </>
  );
};

const NestedDropdownComponent = ({ options, onChange, placeholder, selectedResult }) => {
  const [selectedValues, setSelectedValues] = useState([]);
  const mainDropdownRef = useRef(null);
  const nestedDropdownRef = useRef(null);
  const buttonRefs = useRef({});
  const [openNestedDropdownIndex, setOpenNestedDropdownIndex] = useState(null);

  useEffect(() => {
    const newSelectedValues = selectedResult.map(item => item.value);
    setSelectedValues(newSelectedValues);
  }, [selectedResult]);

  function createDropdownKey(value) {
    if (/^\d/.test(value)) {
      const match = value.match(/^\d+\S*/);
      return match ? match[0] : '';
    }

    const mainModelMatch = value.match(/^\D*/);
    return mainModelMatch ? mainModelMatch[0].trim() : '';
  }

  const dropdownStructure = {};

  if (options.length === 1 && options[0].value === '-') {
    dropdownStructure['Empty'] = {
      label: 'Leeg',
      value: '-',
      children: [],
    };
  } else {
    options.forEach(option => {
      if (option.value !== '-') {
        const key = createDropdownKey(option.value).toUpperCase();

        const dropdownKey = key || option.value.toUpperCase();
        if (!dropdownStructure[dropdownKey]) {
          dropdownStructure[dropdownKey] = {
            label: dropdownKey,
            value: dropdownKey.toLowerCase(),
            children: [],
          };
        }
        if (option.label.toUpperCase() !== dropdownKey) {
          dropdownStructure[dropdownKey].children.push(option);
        }
      }
    });
  }

  const dropdownArray = Object.values(dropdownStructure);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        mainDropdownRef.current &&
        !mainDropdownRef.current.contains(event.target) &&
        nestedDropdownRef.current &&
        !nestedDropdownRef.current.contains(event.target)
      ) {
        if (mainDropdownRef.current.classList.contains('show')) {
          mainDropdownRef.current.classList.remove('show');
        }
        if (nestedDropdownRef.current.classList.contains('show')) {
          nestedDropdownRef.current.classList.remove('show');
        }

        setOpenNestedDropdownIndex(null);
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeDropdown = () => {
    if (mainDropdownRef.current && mainDropdownRef.current.classList.contains('show')) {
      mainDropdownRef.current.classList.remove('show');
    }
    if (nestedDropdownRef.current && nestedDropdownRef.current.classList.contains('show')) {
      nestedDropdownRef.current.classList.remove('show');
    }
    setOpenNestedDropdownIndex(null);
    setIsDropdownOpen(false);
    setActiveLabel('');
  };

  const handleNestedChange = (isChecked, value, label) => {
    let newSelectedValues = isChecked ? [...selectedValues, value] : selectedValues.filter(item => item !== value);

    setSelectedValues(newSelectedValues);

    const event = {
      map: callback =>
        newSelectedValues.map(value => {
          const option = options.find(option => option.value === value);
          return option ? callback(option) : null;
        }),
    };

    onChange(event);
  };

  const toggleNestedDropdown = idx => {
    if (openNestedDropdownIndex === idx) {
      setOpenNestedDropdownIndex(null);
    } else {
      setOpenNestedDropdownIndex(idx);
    }
  };

  const createNestedDropdownPortal = (nestedOptions, idx) => {
    const buttonRect = buttonRefs.current[idx].getBoundingClientRect();
    if (!buttonRect) return null;

    const isNarrowScreen = window.innerWidth < 1400;

    const availableSpaceRight = window.innerWidth - buttonRect.right;
    const nestedDropdownWidth = 200;

    let left;
    if (availableSpaceRight >= nestedDropdownWidth || isNarrowScreen) {
      left = isNarrowScreen ? buttonRect.left + window.scrollX : buttonRect.right + window.scrollX;
    } else {
      left = buttonRect.left - nestedDropdownWidth + window.scrollX;
    }

    const top = isNarrowScreen ? buttonRect.bottom + window.scrollY : buttonRect.top + window.scrollY;

    return ReactDOM.createPortal(
      <ul
        className={cn('dropdown-menu', styles.listDropdown2, { show: true })}
        style={{
          position: 'absolute',
          top: `${top}px`,
          left: `${left}px`,
          width: `${nestedDropdownWidth}px`,
        }}
        ref={nestedDropdownRef}
      >
        <NestedOptions
          options={nestedOptions}
          onChange={handleNestedChange}
          selectedValues={selectedValues}
          closeDropdown={closeDropdown}
        />
      </ul>,
      document.body,
    );
  };

  return (
    <div className="dropdown">
      <div
        className={cn(' dropdown-toggle', styles.button)}
        id="dropdownMenuButton"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        data-bs-auto-close="false"
      >
        <div className={styles.placeholder}>{placeholder}</div>
      </div>

      <ul className={cn('dropdown-menu', styles.listDropdown1)} aria-labelledby="dropdownMenuButton" ref={mainDropdownRef}>
        {dropdownArray.map((option, idx) => (
          <div key={idx}>
            {option.children && option.children.length > 0 ? (
              <div className={styles.dropdownSubmenu}>
                <div className={cn('dropdown', { dropend: window.innerWidth >= 992 })}>
                  <div
                    className={cn(' dropdown-toggle', styles.buttonSubmenu)}
                    onClick={() => toggleNestedDropdown(idx)}
                    ref={el => (buttonRefs.current[idx] = el)}
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    key={idx}
                    data-bs-auto-close="false"
                  >
                    <div className={styles.placeholder}>{option.label}</div>
                  </div>

                  {openNestedDropdownIndex === idx && createNestedDropdownPortal(option.children, idx)}
                </div>
              </div>
            ) : (
              <li onClick={() => handleNestedChange(!selectedValues.includes(option.value), option.value, option.label)}>
                <div className={styles.liOption}>{option.label}</div>
              </li>
            )}
          </div>
        ))}
      </ul>
    </div>
  );
};

export default NestedDropdownComponent;
